import {
  clone,
  propEq,
  findIndex,
  innerJoin,
  eqProps,
  forEach,
  reduce,
  has,
  pipe,
  toPairs,
  assoc,
  ifElse,
  type,
  equals,
  map,
  over,
  lensProp,
  groupBy,
  prop,
  filter,
  propSatisfies,
  fromPairs,
  chain,
  set,
  lensPath,
  pathOr,
  append,
} from 'ramda'
import getNormalizedQuantity from './getNormalizedQuantity.js'
import { keys } from 'lodash'

// todo: refactor so that this function can be removed
export function mutateQuantitiesWithNormalization({ bookingDeal, getNormalizedQuantity }) {
  bookingDeal.products.forEach(p => {
    if (p.QuantitiesPerClassification.length > 0) {
      const productSegments = p.QuantitiesPerClassification[0].QuantitiesPerSegment.map(
        over(lensProp('Quantity'), getNormalizedQuantity(p)),
      )
      p.QuantitiesPerClassification[0].QuantitiesPerSegment = [...productSegments]
    }
  })
}

// from
// bookingDeal { products: [ { isSegmentless, productGroupProductKey, quantitiesPerClassification: [ { classificationCode, quantitiesPerSegment: [{ segmentCode, quantity }] } ] } ] }
// to
// { [productGroupProductKey]: [{ quantitiesPerSegment }] }
export const quantitiesPerClassificationGroupedByProductGroupProductKey = ({
  bookingDeal,
  getNormalizedQuantity,
}) => {
  const normalizeQuantitiesForOneProduct = p =>
    map(
      over(
        lensProp('QuantitiesPerSegment'),
        map(over(lensProp('Quantity'), getNormalizedQuantity(p))),
      ),
    )
  // map(map(...)): the first map is over an object, so all values are mapped
  // those values are arrays, so the second map is over those arrays
  const normalizeQuantities = map(
    map(
      chain(
        normalize => pipe(prop('QuantitiesPerClassification'), normalize),
        normalizeQuantitiesForOneProduct,
      ),
    ),
  )
  return pipe(
    prop('products'),
    filter(x => !x.isSegmentless),
    groupBy(prop('ProductGroupProductKey')),
    normalizeQuantities,
  )(bookingDeal)
}

export const summedQuantitiesPerClassificationPerProductGroupProductKey = ({
  quantitiesPerClassificationGroupedByProductGroupProductKey,
  intersectedQuantitiesPerClassification,
  quantitiesByProductGroupProductKey,
}) => {
  // PGP: ProductGroupProduct
  const summedQuantitiesPerClassificationByPGPKey = pipe(
    keys,
    map(pgpKey => [pgpKey, clone(intersectedQuantitiesPerClassification)]),
    fromPairs,
  )(quantitiesPerClassificationGroupedByProductGroupProductKey)
  // { productGroupProductKey: { classificationCode: { segment: quantity } } } => [ [ pgpKey, cCode, segmentCode, quantity ] ]
  const flattenQuantities = pipe(
    toPairs,
    chain(([k, v]) => map(kvp => [k, ...kvp], toPairs(v))),
    chain(([pgpKey, cCode, c]) => map(s => [pgpKey, cCode, ...s], toPairs(c))),
  )

  return pipe(
    flattenQuantities,
    map(([pgpKey, cCode, segmentCode, q]) => {
      const cKey = findIndex(
        propEq('ClassificationCode', cCode),
        summedQuantitiesPerClassificationByPGPKey[pgpKey],
      )
      return {
        pgpKey,
        cCode,
        segmentCode,
        q,
        cKey,
        segmentKey: findIndex(
          propEq('SegmentCode', segmentCode),
          pathOr(
            [],
            [pgpKey, cKey, 'QuantitiesPerSegment'],
          )(summedQuantitiesPerClassificationByPGPKey),
        ),
      }
    }),
    reduce((a, n) => {
      // complete segments if they are not there
      // them segmentCode comes from quantitiesByProductGroupProductKey
      const { pgpKey, cCode, cKey, segmentCode, q, segmentKey } = n
      if (segmentKey !== -1) {
        return set(lensPath([pgpKey, cKey, 'QuantitiesPerSegment', segmentKey, 'Quantity']), q)(a)
      }
      return a
    }, summedQuantitiesPerClassificationByPGPKey),
  )(quantitiesByProductGroupProductKey)
  // what happens here:
  // - flatten quantitiesByProductGroupProductKey (to [ pgpKey, cCode, segmentCode, quantity ])
  // - find index in summedQuantitiesPerClassificationByPGPKey for classification code
  // - find index in summedQuantitiesPerClassificationByPGPKey for segment code
  // - reduce on summedQuantitiesPerClassificationByPGPKey, adding missing segment quantities from flattened quantitiesByProductGroupProductKey
}

// maps a model
// from [{ ClassificationCode: 'age', QuantitiesPerSegment: [{ SegmentCode: 'kids', Quantity: 5 }] }]
// to { age: { kids: 5 } }
export const mapQuantitiesToDictionary = dealQuantitiesPerClassification => {
  const mapArrayToDictionary = (keyField, valueField) =>
    reduce((a, n) => assoc(n[keyField], n[valueField], a), {})
  const maybeMapToDic = f => ifElse(pipe(type, equals('Array')), f, () => {})
  const mapSegmentQuantitiesToDic = mapArrayToDictionary('SegmentCode', 'Quantity')
  return maybeMapToDic(
    pipe(
      map(over(lensProp('QuantitiesPerSegment'), mapSegmentQuantitiesToDic)),
      mapArrayToDictionary('ClassificationCode', 'QuantitiesPerSegment'),
    ),
  )(dealQuantitiesPerClassification)
}

// takes an array of bookingDeal products
// they have { isSegmentless, quantitiesPerClassification: [{ classificationCode, quantitiesPerSegment: [{ segmentCode, quantity }] }] }
// take the ones for which isSegmentless = false
// map them to { [productGroupProductKey]: { [classificationCode]: { [segmentCode]: quantity } } }
// summing the quantities for each product
export function orderQuantitiesByProductGroupProductKey({ products, normalizeQuantities }) {
  const setOrAddQuantity = (quantities, p) => segmentQuantity => {
    const quantityExists = typeof quantities[segmentQuantity.SegmentCode] === 'number'
    if (!quantityExists) quantities[segmentQuantity.SegmentCode] = 0
    quantities[segmentQuantity.SegmentCode] += normalizeQuantities
      ? segmentQuantity.Quantity / p.QuantityInProductGroup
      : segmentQuantity.Quantity
  }
  const buildQuantities = reduce((a, p) => {
    if (!p.isSegmentless) {
      const quantitiesPerClassification = a[p.ProductGroupProductKey] || {}
      p.QuantitiesPerClassification.forEach(classificationQuantities => {
        const classificationCode = classificationQuantities.ClassificationCode
        const quantities = quantitiesPerClassification[classificationCode] || {}
        classificationQuantities.QuantitiesPerSegment.forEach(setOrAddQuantity(quantities, p))
        quantitiesPerClassification[classificationCode] = quantities
      })
      a[p.ProductGroupProductKey] = quantitiesPerClassification
    }
    return a
  }, {})
  return buildQuantities(products)
}
